import {ICellRendererAngularComp} from "ag-grid-angular";
import {IAfterGuiAttachedParams, ICellRendererParams} from 'ag-grid-community';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy} from "@angular/core";
import {Subscription} from "rxjs";
import {auditTime, debounceTime, filter} from "rxjs/operators";
import {DetectMethodChanges, isVoid} from "../../utils";
import {HgHedgeMatrixComponent} from "./hg-hedge-matrix.component";
import {MessageBusService} from "../../message-bus.service";
import {OptionsChainService} from '../../option-chains.service';
import {IvCacheService} from '../../iv-cache.service';
import {PanelChangeDetectionService} from '../../panel-change-detection.service';

interface ComponentParams {
    comp: HgHedgeMatrixComponent;
    expiration: string;
    dayOfWeek: string;
}

@Component({
    selector: "ets-iv-day-of-week-cell-renderer",
    template: `
      <span class="dow">{{ this.dayOfWeek }}</span>
      <sup *ngIf="this.ivLoading" class="iv-sup"><dx-load-indicator width="12" height="12"></dx-load-indicator></sup>
      <sup *ngIf="this.iv != null" class="iv-sup">{{ this.ivFormatted }}</sup>
      <sup *ngIf="this.broken && this.iv == null && !this.ivLoading" class="iv-sup iv-na">N/A</sup>
    `,
    styles: [`
        :host {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            white-space: nowrap;
            overflow: visible;
        }

        .iv-sup {
            color: black;
            font-size: 0.8em;
            font-weight: bold;
            vertical-align: super;
            margin-left: 7px;
            margin-right: -7px;
            margin-top: -7px;
            text-align: center;
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class IvDayOfWeekCellRendererComponent implements ICellRendererAngularComp, OnDestroy {

    constructor(
        private _changeDetector: ChangeDetectorRef,
        private _messageBus: MessageBusService,
        private _optionChainService: OptionsChainService,
        private _ivCacheService: IvCacheService,
        private _panelCd: PanelChangeDetectionService
    ) {
    }

    private static readonly GATE_TIMEOUT_MS = 10000;

    // Backoff schedule: shorter early attempts so transient failures recover fast,
    // longer tail so we stop hammering the backend on persistent failures
    // (e.g. 0DTE strike where Brent root-finder cannot converge).
    private static readonly BACKOFF_MS = [
        1500, 3000, 5000, 8000, 12000, 20000, 30000, 45000, 60000, 90000
    ];
    private static readonly MAX_RETRIES = IvDayOfWeekCellRendererComponent.BACKOFF_MS.length;

    private _params: (ICellRendererParams & ComponentParams);
    private _destroyed = false;
    private _retryTimer: any = null;
    private _qgCursorDate: string = null;
    private _subscriptions: Subscription[] = [];
    private _ivRefreshTimerSub: Subscription = null;
    private _gated = false;
    private _gateTimeout: any = null;
    private _isQgHistoryMode = false;
    private _inFlight = false;
    private _consecutiveFailures = 0;

    dayOfWeek: string;
    iv: number = null;
    ivLoading = false;
    broken = false;

    get ivFormatted(): string {
        if (this.iv == null) {
            return '';
        }
        return (this.iv * 100).toFixed(1) + '%';
    }

    @DetectMethodChanges()
    agInit(params: (ICellRendererParams & ComponentParams)): void {
        this._params = params;
        this.dayOfWeek = params.dayOfWeek || '';

        if (this._retryTimer != null) {
            clearTimeout(this._retryTimer);
            this._retryTimer = null;
        }

        this.iv = null;

        // Unsubscribe previous (AG Grid recycles cell renderers)
        if (this._ivRefreshTimerSub) {
            this._ivRefreshTimerSub.unsubscribe();
            this._ivRefreshTimerSub = null;
        }
        if (this._gateTimeout) {
            clearTimeout(this._gateTimeout);
            this._gateTimeout = null;
        }
        this._gated = false;
        this._inFlight = false;
        this._consecutiveFailures = 0;
        this.broken = false;
        this._subscriptions.forEach(s => s.unsubscribe());
        this._subscriptions = [];

        this._subscriptions.push(
            this._messageBus.of<any>('Qg.CursorDateChangedMessage')
                .subscribe(msg => {
                    const cursorDate = msg.payload?.cursorDate;
                    if (isVoid(cursorDate)) {
                        this._qgCursorDate = null;
                        return;
                    }
                    const newDate = cursorDate.split('T')[0];
                    if (this._qgCursorDate === newDate) {
                        return;
                    }
                    this._qgCursorDate = newDate;
                    if (!this._gated) { this.resetAndFetch(); }
                })
        );

        this._subscriptions.push(
            this._messageBus.of<any>('AtmStrikeChangedEvent')
                .pipe(debounceTime(2000))
                .subscribe(() => { if (!this._gated) { this.resetAndFetch(); } })
        );

        this._subscriptions.push(
            this._messageBus.of<any>('Qg.StreamModeChanged')
                .subscribe(msg => {
                    this._isQgHistoryMode = msg.payload?.mode === 'History';
                    this.closeGate();
                    this.recreateQuoteSub();
                })
        );

        this.recreateQuoteSub();
        this.fetchIv();
    }

    @DetectMethodChanges()
    refresh(): boolean {
        return true;
    }

    @DetectMethodChanges()
    afterGuiAttached(params?: IAfterGuiAttachedParams) {
    }

    ngOnDestroy(): void {
        this._destroyed = true;
        if (this._ivRefreshTimerSub) {
            this._ivRefreshTimerSub.unsubscribe();
            this._ivRefreshTimerSub = null;
        }
        if (this._retryTimer != null) {
            clearTimeout(this._retryTimer);
            this._retryTimer = null;
        }
        if (this._gateTimeout) {
            clearTimeout(this._gateTimeout);
            this._gateTimeout = null;
        }
        this._subscriptions.forEach(s => s.unsubscribe());
        this._subscriptions = [];
    }

    private closeGate(): void {
        this._gated = true;
        if (this._gateTimeout) { clearTimeout(this._gateTimeout); }
        this._gateTimeout = setTimeout(() => {
            this._gated = false;
            this._gateTimeout = null;
            this.fetchIv();
        }, IvDayOfWeekCellRendererComponent.GATE_TIMEOUT_MS);
    }

    private openGate(): void {
        this._gated = false;
        if (this._gateTimeout) {
            clearTimeout(this._gateTimeout);
            this._gateTimeout = null;
        }
    }

    private recreateQuoteSub(): void {
        if (this._ivRefreshTimerSub) {
            this._ivRefreshTimerSub.unsubscribe();
            this._ivRefreshTimerSub = null;
        }
        const timeOp = this._isQgHistoryMode
            ? debounceTime(2000)
            : auditTime(2000);
        this._ivRefreshTimerSub = this._messageBus.of<any>('QuoteDto')
            .pipe(
                filter(msg => {
                    const und = this._params?.comp?.hedgeMatrixDataService?.getPortfolioUnderlying();
                    return !!und && msg.payload.some((q: any) => q.ticker === und);
                }),
                timeOp
            )
            .subscribe(() => {
                this.openGate();
                this.resetAndFetch();
            });
    }

    private resetAndFetch(): void {
        this._consecutiveFailures = 0;
        this.broken = false;
        this.fetchIv();
    }

    @DetectMethodChanges({isAsync: true})
    private async fetchIv(): Promise<void> {
        if (this._destroyed) { return; }

        // Single-flight: drop overlapping fetch requests. Quote-pipe + retry timer +
        // ATM-change can all race to fire fetchIv concurrently; we only want one in flight.
        if (this._inFlight) { return; }

        // After MAX_RETRIES consecutive failures, stop retrying. resetAndFetch (called
        // from QuoteDto / AtmStrikeChangedEvent / CursorDateChanged paths) clears this.
        if (this._consecutiveFailures >= IvDayOfWeekCellRendererComponent.MAX_RETRIES) {
            return;
        }

        const comp = this._params?.comp;
        const expiration = this._params?.expiration;
        if (isVoid(comp) || isVoid(expiration)) { return; }

        // Skip past-expiration: backend has no quotes for an expired chain, IV cannot be
        // computed, and we'd otherwise burn retries cycling through the backoff schedule
        // until N/A. Show N/A immediately and rely on cursor/atm/quote events to re-enter
        // when an expiration becomes live again (e.g. QG cursor moves backward in time).
        if (this._optionChainService.isExpirationExpired(expiration)) {
            this.broken = true;
            this.ivLoading = false;
            this.iv = null;
            return;
        }

        const underlying = comp.hedgeMatrixDataService.getPortfolioUnderlying();
        if (isVoid(underlying)) { return; }

        if (this.iv == null) {
            this.ivLoading = true;
        }

        // Clear any pending retry — this attempt supersedes it.
        if (this._retryTimer != null) {
            clearTimeout(this._retryTimer);
            this._retryTimer = null;
        }

        this._inFlight = true;
        try {
            // Routed through IvCacheService for cross-consumer coalescing (HM cells +
            // OPG header + APG header all share one HTTP per (und, exp) within TTL).
            const reply = await this._ivCacheService.getIv(underlying, expiration);

            if (this._destroyed) { return; }

            if (reply.volatility != null) {
                this.iv = reply.volatility;
                this.ivLoading = false;
                this._consecutiveFailures = 0;
                this.broken = false;
            } else {
                this.handleFailure();
            }
        } catch {
            this.handleFailure();
        } finally {
            this._inFlight = false;
        }
    }

    private handleFailure(): void {
        if (this._destroyed) { return; }
        const idx = this._consecutiveFailures;
        this._consecutiveFailures += 1;

        if (this._consecutiveFailures >= IvDayOfWeekCellRendererComponent.MAX_RETRIES) {
            this.broken = true;
            this.ivLoading = false;
            return;
        }

        const delay = IvDayOfWeekCellRendererComponent.BACKOFF_MS[idx];
        if (this._retryTimer != null) { clearTimeout(this._retryTimer); }
        this._retryTimer = setTimeout(() => this.fetchIv(), delay);
    }
}
